import React, {Component} from 'react';
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import Loader from "@amzn/meridian/loader";
import ErrorPage from "../utils/ErrorPage";
import Table from "@amzn/meridian/table";
import TableRow from "@amzn/meridian/table/table-row";
import TableCell from "@amzn/meridian/table/table-cell";
import Column from "@amzn/meridian/column";
import {chainWalk, formQueryParams, getNodeId, hasAmxlViewFlagEnabled} from "../../Utility";
import {FormattedMessage} from "react-intl";
import RefreshTimer from "../RefreshTimer";
import {getFormattedDate} from "../utils/DateTimeUtils";
import Link from "@amzn/meridian/link";
import {PackageStatus} from "../../enums";
import {FEATURES, TOTAL, TRUE} from "../../constants";
import {navigationAdded} from "../../actions/OptionsActions";

/**
 * Floor Location View with floor location as the primary column.
 * Currently, used for showing sidelined packages.
 */
class FloorLocation extends Component {

    constructor(props) {
        super(props);
        this.state = {
            sortColumn: "floorLocation",
            sortDirection: "ascending"
        }
    }

    render() {
        const {loading, error, stowPackageMetricsMap, stowFloorMetricsMap, stowAssociateMetricsMap} = this.props;

        if (error) return <ErrorPage errorId="stow.error" errorMessage="Some error occurred"/>;

        const allFloorLocationsAndSidelineUntilTime = [
            ...Object.keys(stowFloorMetricsMap),
            ...Object.keys(stowPackageMetricsMap),
            ...Object.keys(stowAssociateMetricsMap),
        ];

        const distinctFloorLocationsAndSidelineUntilTime = [...new Set(allFloorLocationsAndSidelineUntilTime)];

        if (loading && !distinctFloorLocationsAndSidelineUntilTime.length) {
            return (
                <Column height="400px" alignmentHorizontal="center">
                    <Loader/>
                </Column>
            );
        }

        const onSort = (sortDetails) => {
            this.setState({
                sortColumn: sortDetails.sortColumn,
                sortDirection: sortDetails.sortDirection
            });
        }

        const sortColumn = this.state.sortColumn;
        const direction = this.state.sortDirection;
        distinctFloorLocationsAndSidelineUntilTime.sort((c1, c2) => {
            const c1CombinedMetricsMap = this.getCombinedMetricsMap(c1);
            const c2CombinedMetricsMap = this.getCombinedMetricsMap(c2);
            if (c1CombinedMetricsMap[sortColumn] < c2CombinedMetricsMap[sortColumn]) {
                return direction === "ascending" ? -1 : 1;
            }
            if (c1CombinedMetricsMap[sortColumn] > c2CombinedMetricsMap[sortColumn]) {
                return direction === "ascending" ? 1 : -1;
            }
            return 0;
        });

        return (
            <Column>
                <RefreshTimer deps={[stowPackageMetricsMap]} />
                <Table headerRows={2} showDividers={false} showStripes={false} spacing="small" fixHeaderRows={true}
                       fixHeaderRowsFullWidth={true}
                       sortColumn={this.state.sortColumn}
                       sortDirection={this.state.sortDirection}
                       onSort={onSort}
                       data-cy={"floorLocationTable"}>
                    {this.renderHeadings()}
                    {this.renderSubHeadings()}
                    {distinctFloorLocationsAndSidelineUntilTime.map((compositeKey) => this.renderFloorLocationRow(compositeKey))}
                    {distinctFloorLocationsAndSidelineUntilTime.length ? this.renderTotalFloorLocationRow() : null}
                </Table>
            </Column>
        )
    }

    renderHeadings() {
        return (
            <TableRow>
                <TableCell/>
                <TableCell/>
                <TableCell>
                    <FormattedMessage id="floorLocation.packages" defaultMessage="Packages"/>
                </TableCell>
                <TableCell/>
                {this.isRescheduledColumnEnabled() && <TableCell/>}
                <TableCell>
                    <FormattedMessage id="floorLocation.carts" defaultMessage="Carts"/>
                </TableCell>
                <TableCell>
                    <FormattedMessage id="floorLocation.performance" defaultMessage="Performance"/>
                </TableCell>
                <TableCell/>
                <TableCell>
                    <FormattedMessage id="floorLocation.stowers" defaultMessage="Stowers"/>
                </TableCell>
                <TableCell/>
            </TableRow>
        )
    }

    renderSubHeadings() {
        return (
            <TableRow>
                <TableCell sortColumn="floorLocation">
                    <FormattedMessage id="floorLocation.floor_location" defaultMessage="Floor Location"/>
                </TableCell>
                <TableCell sortColumn="reInductDay">
                    {
                    this.isReInductDatePlusOneFixEnabled() ?
                        <FormattedMessage id="floorLocation.ofd" defaultMessage="OFD"/>
                        : <FormattedMessage id="floorLocation.re_induct_day" defaultMessage="Re-induct Day"/>
                    }
                </TableCell>
                <TableCell sortColumn="currentInductedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.inducted" defaultMessage="Inducted"/>
                </TableCell>
                <TableCell sortColumn="currentStowedPackages" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.stowed" defaultMessage="Stowed"/>
                </TableCell>
                {this.isRescheduledColumnEnabled() &&
                    <TableCell sortColumn="rescheduledCount" alignmentHorizontal="right">
                        <FormattedMessage id="floorLocation.rescheduled" defaultMessage="Rescheduled"/>
                    </TableCell>
                }
                <TableCell sortColumn="attachedCartCount" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.no_of_carts" defaultMessage="# of Carts"/>
                </TableCell>
                <TableCell sortColumn="stowRate" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.stow_rate" defaultMessage="Stow rate"/>
                </TableCell>
                <TableCell sortColumn="scanCompliance" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.scan_compliance" defaultMessage="Scan comp."/>
                </TableCell>
                <TableCell sortColumn="activeStowers" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.active" defaultMessage="Active"/>
                </TableCell>
                <TableCell sortColumn="inactiveStowers" alignmentHorizontal="right">
                    <FormattedMessage id="floorLocation.inactive" defaultMessage="Inactive"/>
                </TableCell>
            </TableRow>
        )
    }

    renderFloorLocationRow(compositeKey) {
        const combinedMetricsMap = this.getCombinedMetricsMap(compositeKey);

        return (
            <TableRow highlightOnHover={true} key={combinedMetricsMap.floorLocation + combinedMetricsMap.reInductDay}
                      onClick={() => this.rowClicked(compositeKey)} data-cy={"floorLocationDataRow"}>
                <TableCell>{combinedMetricsMap.floorLocation}</TableCell>
                <TableCell>{getFormattedDate(combinedMetricsMap.reInductDay, this.isReInductDatePlusOneFixEnabled())}</TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.currentInductedPackages, compositeKey, PackageStatus.CURRENT_INDUCTED)}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {this.renderPackageCount(combinedMetricsMap.currentStowedPackages, compositeKey, PackageStatus.CURRENT_STOWED)}
                </TableCell>
                {this.isRescheduledColumnEnabled() &&
                    <TableCell alignmentHorizontal="right">
                        {this.renderPackageCount(combinedMetricsMap.rescheduledCount, compositeKey, PackageStatus.RESCHEDULED)}
                    </TableCell>
                }
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.attachedCartCount}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.stowRate}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.scanCompliance}{('-' !== combinedMetricsMap.scanCompliance) && '%'}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.activeStowers}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {combinedMetricsMap.inactiveStowers}
                </TableCell>
            </TableRow>
        )
    }

    renderTotalFloorLocationRow() {
        const totalMetricsMap = this.getTotalStowMetricsMap();
        return (
            <TableRow data-cy={"floorLocationTotalRow"}>
                <TableCell>{totalMetricsMap.floorLocation}</TableCell>
                <TableCell>{totalMetricsMap.reInductDay}</TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.currentInductedPackages}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.currentStowedPackages}
                </TableCell>
                {this.isRescheduledColumnEnabled() &&
                    <TableCell alignmentHorizontal="right">
                        {totalMetricsMap.rescheduledCount}
                    </TableCell>
                }
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.attachedCartCount}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.stowRate}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.scanCompliance}{('-' !== totalMetricsMap.scanCompliance) && '%'}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.activeStowers}
                </TableCell>
                <TableCell alignmentHorizontal="right">
                    {totalMetricsMap.inactiveStowers}
                </TableCell>
            </TableRow>
        )
    }

    getCombinedMetricsMap = compositeKey => {
        const {stowFloorMetricsMap, stowPackageMetricsMap} = this.props;
        const defaultMetricValue = "-";
        const {floorLocation, reInductDay} = this.parseCompositeKey(compositeKey);

        return {
            floorLocation: floorLocation,
            reInductDay: reInductDay,
            currentInductedPackages: chainWalk(() => stowPackageMetricsMap[compositeKey].currentInductedPackages, defaultMetricValue),
            currentStowedPackages: chainWalk(() => stowPackageMetricsMap[compositeKey].currentStowedPackages, defaultMetricValue),
            rescheduledCount: chainWalk(() => stowPackageMetricsMap[compositeKey].rescheduledCount, defaultMetricValue),
            attachedCartCount: chainWalk(() => stowFloorMetricsMap[compositeKey].floorMetrics.attachedCartCount, defaultMetricValue),
            stowRate: chainWalk(() => stowPackageMetricsMap[compositeKey].stowRate, defaultMetricValue),
            scanCompliance:chainWalk(() => stowPackageMetricsMap[compositeKey].currentScanCompliance, defaultMetricValue),
            activeStowers: defaultMetricValue, //chainWalk(() => stowAssociateMetricsMap[compositeKey].activeStowers, defaultMetricValue),
            inactiveStowers: defaultMetricValue //chainWalk(() => stowAssociateMetricsMap[compositeKey].inactiveStowers, defaultMetricValue)
        }
    }

    getTotalStowMetricsMap() {
        const {totalStowPackageMetrics} = this.props;
        const defaultMetricValue = "-";
        return {
            floorLocation: TOTAL,
            reInductDay: defaultMetricValue,
            currentInductedPackages: chainWalk(() => totalStowPackageMetrics.currentInductedPackages, defaultMetricValue),
            currentStowedPackages: chainWalk(() => totalStowPackageMetrics.currentStowedPackages, defaultMetricValue),
            rescheduledCount: chainWalk(() => totalStowPackageMetrics.rescheduledCount, defaultMetricValue),
            attachedCartCount: chainWalk(() => totalStowPackageMetrics.floorMetrics.attachedCartCount, defaultMetricValue),
            stowRate: chainWalk(() => totalStowPackageMetrics.stowRate, defaultMetricValue),
            scanCompliance: chainWalk(() => totalStowPackageMetrics.currentScanCompliance, defaultMetricValue),
            activeStowers: defaultMetricValue, //chainWalk(() => stowAssociateMetricsMap[compositeKey].activeStowers, defaultMetricValue),
            inactiveStowers: defaultMetricValue //chainWalk(() => stowAssociateMetricsMap[compositeKey].inactiveStowers, defaultMetricValue)
        }
    }



    parseCompositeKey = (key) => {
        const splitKey = key.split("|");
        return {
            floorLocation: splitKey[0],
            reInductDay: splitKey[1]
        };
    }

    isSidelineViewV2Enabled = () => {
        return this.props.featureStatus && this.props.featureStatus[FEATURES.SIDELINE_STOW_V2];
    }

    isRescheduledColumnEnabled = () => {
        return hasAmxlViewFlagEnabled(getNodeId(), this.props.featureStatus);
    }

    isReInductDatePlusOneFixEnabled = () => {
        return this.props.featureStatus && this.props.featureStatus[FEATURES.REINDUCT_DATE_PLUS_ONE_FIX];
    }

    renderPackageCount = (value, compositeKey, state) => {
        const {floorLocation, reInductDay} = this.parseCompositeKey(compositeKey);
        const params = {
            PACKAGE_STATUS: state,
            FLOOR_LOCATION: floorLocation,
            SIDELINE_UNTIL_TIME: reInductDay
        };

        if(this.isSidelineViewV2Enabled()) {
            params.SIDELINE_V2 = TRUE;
        } else {
            params.SIDELINE = TRUE;
        }

        return value !== 0 && value !== '-' ? <Link href={this.getPackageListURL(params)} type="secondary"> {value} </Link> : value;
    }

    getPackageListURL = (params) => {
        return "#packageList/?" + formQueryParams(params);
    }

    rowClicked = (floorLocationAndReInductDay) => {
        const {floorLocation, reInductDay} = this.parseCompositeKey(floorLocationAndReInductDay);
        this.props.dispatch(navigationAdded({
            key: 'Floor Location, Re-Induct Day',
            value: [floorLocation, reInductDay],
            displayValue: `${floorLocation}, ${getFormattedDate(reInductDay, this.isReInductDatePlusOneFixEnabled())}`
        }));
    }
}

const mapStateToProps = state => ({
    loading: state.floorLocationReducer.loading,
    error: state.floorLocationReducer.error,
    options: state.floorLocationReducer.options,
    stowFloorMetricsMap: state.floorLocationReducer.stowFloorMetricsMap,
    stowPackageMetricsMap: state.floorLocationReducer.stowPackageMetricsMap,
    stowAssociateMetricsMap: state.floorLocationReducer.stowAssociateMetricsMap,
    totalStowPackageMetrics: state.floorLocationReducer.totalStowPackageMetrics,
    featureStatus: state.configReducer.featureStatus,
});

export default withRouter(connect(mapStateToProps)(FloorLocation));



// WEBPACK FOOTER //
// ./src/components/sideline/FloorLocation.js